import React, { useMemo, useState } from "react";
import { ActionIcon, Checkbox, Group, Menu, SegmentedControl, Switch } from "@mantine/core";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { Box, useMantineColorScheme, Button } from "@mantine/core";
import PropTypes from "prop-types";
import _ from "lodash";
import { formatName } from "./energyBalanceTab/ENERGY_BALANCE_CONFIG";
import { ATTRIBUTES_LABELS, useAttributesLabels } from "./RESULT_CONFIG";
import CompareChart from "./CompareChart";
import { IconFilter } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { exportTableToCsv } from "../../utils/csvExport";
import {
  formatValueByChartTitle,
  formatTooltipValueByChartTitle,
  roundToMaxTwoDecimals,
  formatLcoeUpToFiveDecimals,
} from "../../utils/helpers";

const createReferenceMinusScenarioVariationCalculator = (
  baseAttribute,
  variationAttribute
) => {
  return ({ referenceScenario, scenarioObject, scenarioAttribute }) => {
    const referenceValue = referenceScenario?.[baseAttribute];
    const scenarioValue = scenarioObject?.[baseAttribute];
    const hasValidValues =
      typeof referenceValue === "number" &&
      !Number.isNaN(referenceValue) &&
      typeof scenarioValue === "number" &&
      !Number.isNaN(scenarioValue);

    if (!hasValidValues) return scenarioObject?.[variationAttribute];
    return  scenarioValue - referenceValue;
  };
};

const DERIVED_KPI_CALCULATORS = {
  NPCVariation: createReferenceMinusScenarioVariationCalculator(
    "NPC",
    "NPCVariation"
  ),
  OpCostVariation: createReferenceMinusScenarioVariationCalculator(
    "OpCost",
    "OpCostVariation"
  ),
  AnnCostVariation: createReferenceMinusScenarioVariationCalculator(
    "AnnCost",
    "AnnCostVariation"
  ),
};

const DERIVED_KPI_ORDER = {
  IRRDiscounted: { insertAfter: "IRR" },
  ROIDiscounted: { insertAfter: "ROI" },
  PaybackDiscounted: { insertAfter: "PaybackTime" },
  NPCVariation: { insertAfter: "NPC" },
  OpCostVariation: { insertAfter: "OpCost" },
  AnnCostVariation: { insertAfter: "AnnCost" },
};

const getScenarioRowAttributes = (showScenario, derivedCalculators) => {
  const allowedAttributes = new Set(Object.keys(ATTRIBUTES_LABELS));
  const baseAttributes = _.uniq(
    Object.values(showScenario || {})
      .filter((scenarioObject) => _.isPlainObject(scenarioObject))
      .flatMap((scenarioObject) => Object.keys(scenarioObject))
  );
  const derivedAttributes = Object.keys(derivedCalculators || {});
  const orderedAttributes = [...baseAttributes];
  const attributesToOrder = [
    ...derivedAttributes,
    ...Object.keys(DERIVED_KPI_ORDER),
  ];

  attributesToOrder.forEach((attribute) => {
    const insertAfter = DERIVED_KPI_ORDER?.[attribute]?.insertAfter;
    const insertAfterIndex = insertAfter
      ? orderedAttributes.indexOf(insertAfter)
      : -1;

    const existingIndex = orderedAttributes.indexOf(attribute);
    if (existingIndex >= 0) {
      orderedAttributes.splice(existingIndex, 1);
    }

    if (insertAfterIndex >= 0) {
      orderedAttributes.splice(insertAfterIndex + 1, 0, attribute);
    } else {
      orderedAttributes.push(attribute);
    }
  });

  return orderedAttributes.filter((attribute) =>
    allowedAttributes.has(attribute)
  );
};

const getScenarioCellValue = ({
  attribute,
  scenarioObject,
  scenarioAttribute,
  referenceScenario,
  derivedCalculators,
}) => {
  const deriveValue = derivedCalculators?.[attribute];
  const value = deriveValue
    ? deriveValue({ referenceScenario, scenarioObject, scenarioAttribute })
    : scenarioObject?.[attribute];
  if (attribute?.toLowerCase() === "lcoe" || attribute?.toLowerCase() === "lcos") {
    return formatLcoeUpToFiveDecimals(value);
  }
  return roundToMaxTwoDecimals(value);
};

const ScenarioTable = ({ handleClickCompare, showScenario, gridType, simulationNameLookupMap }) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const isLightMode = colorScheme === "light";
  const [view, setView] = useState("table");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const labels = useAttributesLabels(gridType);
  const allKpiNames = useMemo(() => {
    const rowAttributes = getScenarioRowAttributes(
      showScenario,
      DERIVED_KPI_CALCULATORS
    );
    return rowAttributes.map((attribute) => labels[attribute] || attribute);
  }, [showScenario, labels]);
  const [selectedKpis, setSelectedKpis] = useState(allKpiNames);

  const columns = useMemo(() => {
    const columnAttributes = Object.keys(showScenario);
    return [
      {
        accessorKey: "attribute",
        header: t("result.scenario.kpi"),
        size: 30,
      },
      ...columnAttributes.map((key) => ({
        id: key,
        accessorFn: (row) => row?.[key],
        header: simulationNameLookupMap?.[key] || key,
        size: 30,
        Cell: ({ cell }) => {
          const rawValue = cell.getValue();
          const rowTitle = cell?.row?.original?.attribute || "";
          const compactValue =
            typeof rawValue === "number" && !Number.isNaN(rawValue)
              ? formatValueByChartTitle(rowTitle, rawValue)
              : rawValue;
          const hoverValue = formatTooltipValueByChartTitle(rowTitle, rawValue);

          return (
            <span title={hoverValue !== undefined && hoverValue !== null ? String(hoverValue) : ""}>
              {compactValue}
            </span>
          );
        },
      })),
    ];
  }, [showScenario]);

  const tableData = useMemo(() => {
    const referenceScenario = showScenario?.referenceExistingMicrogrid || {};
    const bodyAttributes = getScenarioRowAttributes(
      showScenario,
      DERIVED_KPI_CALCULATORS
    );
    return bodyAttributes
      .map((attribute) => {
        const kpiName = labels[attribute] || attribute;
        if (!selectedKpis.includes(kpiName)) return null;
        const completeScenarioData = {
          attribute: kpiName,
        };
        _.forOwn(showScenario, (scenarioObject, scenarioAttribute) => {
          completeScenarioData[scenarioAttribute] = getScenarioCellValue({
            attribute,
            scenarioObject,
            scenarioAttribute,
            referenceScenario,
            derivedCalculators: DERIVED_KPI_CALCULATORS,
          });
        });
        return completeScenarioData;
      })
      .filter(Boolean);
  }, [showScenario, selectedKpis, labels]);
  const tableInstance = useMantineReactTable({
    data: tableData,
    columns,
    enablePagination: false,
    enableColumnPinning: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableStickyHeader: true,
    enableDensityToggle: false,
    initialState: {
      density: "xs",
      columnPinning: { left: ["attribute"] },
    },
    mantineTableContainerProps: {
      sx: { height: tableData?.length > 9 ? "700px" : "500px" },
    },
    mantineTableBodyProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        maxHeight: "1000px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: {
        width: "100%",
        height: "100%",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
        borderTop: "1px solid #dee2e6 !important",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
  <Group position="apart" align="center" style={{ width: '100%', paddingTop: 2, paddingRight: 5, alignItems:"baseline" }}>
      <Group spacing={8} align="center">
        <se-button aria-label="scenario-close-toolbar" onClick={handleClickCompare}>{t("common.close")}</se-button>
        <Button
          aria-label="scenario-export-csv"
          size="xs"
          variant="default"
          onClick={() => exportTableToCsv(table, "scenario_comparison.csv")}
        >
          Export CSV
        </Button>
      </Group>

      {/* Right side: filter icon */}
      <Menu opened={filterMenuOpen} onChange={setFilterMenuOpen} withinPortal>
        <Menu.Target>
          <ActionIcon
            aria-label="scenario-filter-kpi"
            style={{ backgroundColor: "transparent" }}
            size="sm"
            variant="light"
            onClick={() => setFilterMenuOpen((o) => !o)}
            title={t("result.scenario.filterKpi")}
          >
            <IconFilter size={22} />
          </ActionIcon>
        </Menu.Target>
          <Menu.Dropdown style={{ maxHeight: 300, overflowY: 'auto', position: 'absolute', zIndex: 1000 }}>
            <Menu.Label>{t("result.scenario.filterKpiRows")}</Menu.Label>
            <Group spacing={6} position="apart" style={{ marginBottom: 8, width: '100%' }}>
              <Button aria-label="scenario-hide-all-kpis" size="sm" variant="subtle" style={{ fontWeight: 500, flex: 1, marginRight: 4, minWidth: 70 }}
                onClick={() => setSelectedKpis([])}
                disabled={selectedKpis.length === 0}
              >
                {t("common.hideAll")}
              </Button>
              <Button aria-label="scenario-show-all-kpis" size="sm" variant="subtle" style={{ fontWeight: 500, flex: 1, marginLeft: 4, minWidth: 70 }}
                onClick={() => setSelectedKpis(allKpiNames)}
                disabled={selectedKpis.length === allKpiNames.length}
              >
                {t("common.showAll")}
              </Button>
            </Group>
            <div style={{ width: '100%', margin: '4px 0 10px 0' }}>
              <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: 0 }} />
            </div>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 8px' }}>
              {allKpiNames.map((kpi) => (
                <Group key={kpi} position="left" style={{ alignItems: 'center', width: '100%', gap: 10 }}>
                  <Switch
                    size="sm"
                    checked={selectedKpis.includes(kpi)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedKpis((prev) => [...prev, kpi]);
                      } else {
                        setSelectedKpis((prev) => prev.filter((name) => name !== kpi));
                      }
                    }}
                    label={kpi}
                  />
                </Group>
              ))}
            </Box>
        </Menu.Dropdown>
      </Menu>
    </Group>
    ),
  });

  return (
    <div style={{ width: "100%", height: "90%" }}>
      <Box className="box-header .step-1" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {view === "chart" ? (
          <se-button aria-label="scenario-close-view" onClick={handleClickCompare}>{t("common.close")}</se-button>
        ) : null}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <SegmentedControl
            value={view}
            onChange={setView}
            data={[
              { label: t("common.table"), value: "table" },
              { label: t("common.chart"), value: "chart" },
            ]}
            size="sm"
          />
        </div>
      </Box>
      {view === "table" ? (
        <MantineReactTable table={tableInstance} />
      ) : (
        <CompareChart data={tableData}/>
      )}
    </div>
  );
};

ScenarioTable.propTypes = {
  handleClickCompare: PropTypes.func,
  showScenario: PropTypes.shape({
    IRR: PropTypes.number,
    ROI: PropTypes.number,
    PaybackTime: PropTypes.number,
    RenewFraction: PropTypes.number,
    NPC: PropTypes.number,
    AnnCost: PropTypes.number,
    LCOE: PropTypes.number,
    LCOS: PropTypes.number,
    OpCost: PropTypes.number,
    Capital: PropTypes.number,
    AutoProd: PropTypes.number,
    AutoCons: PropTypes.number,
    PositiveEnergy: PropTypes.number,
    LocalCO2: PropTypes.number,
    GridCO2: PropTypes.number,
    ThermalSupplyCO2: PropTypes.number,
    UnmetEnergyOffgrid: PropTypes.number,
    UnmetEnergyGridConnected: PropTypes.number,
    ExcessEnergyOffgrid: PropTypes.number,
    ExcessEnergyGridConnected: PropTypes.number,
  }),
  simulationNameLookupMap: PropTypes.object,
  gridType: PropTypes.string,
};

export default ScenarioTable;
